export default {
  title: 'Settings',
  drawer: {
    account: 'Account',
    billing: 'Billing & Usage',
    developer: 'Developer',
    dangerZone: 'Delete Account',
    notifications: 'Notifications',
    myProfile: 'My Profile',
    personalSettings: 'Personal Settings',
    myOrganization: 'My Organization',
    organizationDetails: 'Organization Details',
    subscriptionBilling: 'Subscription & Billing',
    team: 'Team',
    consumption: 'Usage',
    helpSupport: 'Help & Support',
    supportRequests: 'Support Requests',
    helpCenter: 'Help Center',
    apiAccess: 'API Access',
    advancedSettings: 'Advanced Settings'
  },
  deleteAccount: {
    title: 'Delete Account',
    subtitle: 'Permanently delete your account and all associated data',
    steps: {
      warning: 'Consequences',
      feedback: 'Feedback',
      confirm: 'Confirmation'
    },
    warning: {
      title: 'This action is permanent and cannot be undone',
      item1: 'All your personal data will be permanently deleted',
      item2: 'You will lose access to all your files, channels, and showcases',
      item3: 'All your active subscriptions will be cancelled',
      item4: 'You will be immediately logged out from all devices',
      understand: 'I understand that this action is irreversible',
      organizationsTitle: 'Organizations you own will be deleted',
      organizationsDescription: 'You are the owner of the following organizations. These organizations and all their data will be permanently deleted.',
      organizationMembers: '{count} members',
      organizationTransferHint: 'To keep these organizations, transfer ownership to another member before deleting your account.'
    },
    feedback: {
      title: 'Help us improve',
      subtitle: 'We\'re sorry to see you go. Your feedback helps us make our service better.',
      additional: 'Additional comments (optional)',
      placeholder: 'Tell us more about your experience...'
    },
    reasons: {
      noLongerNeeded: 'I no longer need the service',
      foundAlternative: 'I found a better alternative',
      tooExpensive: 'The service is too expensive',
      privacyConcerns: 'I have privacy concerns',
      difficultToUse: 'The service is too difficult to use',
      notEnoughFeatures: 'Not enough features for my needs',
      other: 'Other reason'
    },
    otp: {
      title: 'Verify your identity',
      subtitle: 'We\'ve sent a verification code to {email}',
      resend: 'Resend code',
      resendIn: 'Resend code in {seconds}s',
      confirmLabel: 'Type the confirmation text',
      confirmPlaceholder: 'Type here',
      confirmHint: 'Type "{text}" to confirm account deletion',
      deleteButton: 'Delete my account permanently',
      error: 'Invalid verification code. Please try again.'
    }
  },
  sections: {
    profile: 'Profile',
    appearance: 'Appearance',
    security: 'Security',
    billing: 'Billing',
    usage: 'Usage Statistics',
    api: 'API Keys'
  },
  profile: {
    title: 'Profile',
    subtitle: 'Manage your personal information and account settings',
    avatar: {
      title: 'Profile Picture',
      upload: 'Upload Photo',
      remove: 'Remove Photo',
      hint: 'JPG, PNG or GIF. Max 5MB.'
    },
    personal: {
      title: 'Personal Information',
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username'
    },
    emails: {
      title: 'Email Addresses',
      add: 'Add Email',
      primary: 'Primary',
      unverified: 'Unverified',
      makePrimary: 'Make Primary',
      resendVerification: 'Resend Verification',
      remove: 'Remove'
    },
    language: {
      title: 'Language Preference',
      select: 'Select Language',
      hint: 'Choose your preferred language for the interface'
    },
    password: {
      title: 'Password',
      lastChanged: 'Last changed {date}',
      change: 'Change Password',
      current: 'Current Password',
      new: 'New Password',
      confirm: 'Confirm New Password',
      update: 'Update Password'
    },
    twoFactor: {
      title: 'Two-Factor Authentication',
      notEnabled: 'Two-factor authentication adds an extra layer of security to your account.',
      enabled: 'Two-factor authentication is enabled',
      enable: 'Enable Two-Factor Authentication',
      disable: 'Disable',
      manage: 'Manage Methods',
      methods: 'Active Methods'
    },
    danger: {
      title: 'Danger Zone',
      description: 'Permanently delete your account and all of its contents.',
      deleteAccount: 'Delete Account'
    }
  },
  appearance: {
    title: 'Appearance Settings',
    theme: 'Theme',
    themeDescription: 'Choose your preferred color theme',
    language: 'Language',
    languageDescription: 'Choose your preferred language',
    dateFormat: 'Date Format',
    timeFormat: 'Time Format'
  },
  security: {
    title: 'Security Settings',
    password: 'Password',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    twoFactor: 'Two-Factor Authentication',
    enable2FA: 'Enable 2FA',
    disable2FA: 'Disable 2FA',
    sessions: 'Active Sessions',
    logoutAll: 'Logout All Sessions'
  },
  billing: {
    title: 'Billing & Subscription',
    subtitle: 'Manage your subscription, payment methods, and invoices',
    subscription: {
      title: 'Current Subscription',
      currentPlan: 'Current Plan',
      nextBilling: 'Next billing on {date} • {amount}',
      paymentMethod: 'Payment method ending in {method}',
      perMonth: 'per month',
      cancel: 'Cancel Subscription',
      status: {
        active: 'Active',
        cancelled: 'Cancelled',
        past_due: 'Past Due'
      }
    },
    packs: {
      title: 'Usage Packs',
      add: 'Add Pack',
      perPack: 'pack',
      upgrade: 'Upgrade'
    },
    payment: {
      title: 'Payment Methods',
      add: 'Add Payment Method',
      expires: 'Expires {date}',
      default: 'Default',
      makeDefault: 'Make Default',
      remove: 'Remove'
    },
    address: {
      title: 'Billing Address',
      company: 'Company (optional)',
      firstName: 'First Name',
      lastName: 'Last Name',
      street: 'Street Address',
      zipCode: 'Postal Code',
      city: 'City',
      country: 'Country',
      taxId: 'Tax ID / VAT Number',
      taxIdHint: 'For EU businesses: VAT number for tax exemption'
    },
    invoices: {
      title: 'Invoices',
      number: 'Invoice Number',
      date: 'Date',
      description: 'Description',
      amount: 'Amount',
      status: {
        paid: 'Paid',
        pending: 'Pending',
        overdue: 'Overdue'
      }
    },
    cancelDialog: {
      title: 'Cancel Subscription',
      warning: 'Your subscription will remain active until the end of the current billing period.',
      description: 'After cancellation, you will lose access to all premium features. Your data will be preserved for 30 days.',
      reason: 'Reason for cancellation (optional)',
      confirm: 'Cancel Subscription'
    },
    freeTier: {
      planName: 'Free Plan',
      planDescription: 'Perfect for getting started with basic features',
      forever: 'Free forever',
      upgradeToPro: 'Upgrade to Pro',
      upgradeNow: 'Upgrade Now - {price}/month',
      quotas: {
        title: 'Usage Quotas',
        included: 'Included in free plan',
        nearLimit: 'Near quota limit',
        upgradeInfo: 'Need more resources? Upgrade to Pro for unlimited usage'
      },
      benefits: {
        title: 'Compare Plans',
        currentPlan: 'Free Plan',
        proPlan: 'Professional Plan'
      },
      features: {
        storage5gb: '5 GB Storage',
        bandwidth10gb: '10 GB Bandwidth per month',
        user1: '1 User',
        basicSupport: 'Community support',
        publicShowcases: 'Public showcases only',
        unlimitedStorage: 'Customizable storage packs',
        unlimitedBandwidth: 'Customizable bandwidth packs',
        unlimitedUsers: 'Multiple user packs',
        prioritySupport: 'Priority email support',
        privateShowcases: 'Private & password-protected showcases',
        advancedAnalytics: 'Advanced analytics & insights',
        apiAccess: 'API access for integrations',
        customBranding: 'Custom branding options'
      },
      payment: {
        noMethod: 'No payment method',
        noMethodDescription: 'Add a payment method to upgrade to Pro'
      },
      invoices: {
        noInvoices: 'No invoices yet',
        noInvoicesDescription: 'Invoices will appear here when you upgrade to a paid plan'
      },
      upgradeDialog: {
        title: 'Upgrade to Professional',
        noPaymentMethod: 'You\'ll need to add a payment method to complete the upgrade',
        addPaymentAndUpgrade: 'Add Payment Method & Upgrade',
        benefit1Title: 'Scale Without Limits',
        benefit1Description: 'Add storage, bandwidth, and users as your needs grow',
        benefit2Title: 'Advanced Features',
        benefit2Description: 'Unlock analytics, API access, and premium support',
        benefit3Title: 'Enhanced Security',
        benefit3Description: 'Private showcases and advanced access controls'
      }
    }
  },
  api: {
    title: 'API Keys',
    description: 'Manage your API keys for external integrations',
    createKey: 'Create New Key',
    keyName: 'Key Name',
    permissions: 'Permissions',
    created: 'Created',
    lastUsed: 'Last Used',
    revoke: 'Revoke',
    copyKey: 'Copy Key',
    keyWarning: 'Make sure to copy your API key now. You won\'t be able to see it again!'
  },
  usage: {
    title: 'Usage Statistics',
    subtitle: 'Monitor your resource consumption and activity',
    export: 'Export Data',
    billingCycle: 'Billing cycle',
    storage: 'Storage',
    bandwidth: 'Bandwidth',
    users: 'Users',
    totalStorage: 'Total Storage',
    totalBandwidth: 'Total Bandwidth',
    activeUsers: 'Active Users',
    projectedCost: 'Projected Cost',
    of: 'of {total}',
    thisMonth: 'this month',
    upgrade: 'Upgrade',
    downloads: 'Downloads',
    uploads: 'Uploads',
    peakDay: 'Peak day',
    averageDaily: 'Average daily',
    recentActivity: 'Recent Activity',
    viewAll: 'View All',
    justNow: 'Just now',
    hoursAgo: '{hours}h ago',
    daysAgo: '{days}d ago',
    projectionWarning: 'Based on current usage, you may reach {value} by month end',
    periods: {
      week: 'Last 7 days',
      month: 'This month',
      '3months': 'Last 3 months',
      '6months': 'Last 6 months',
      year: 'This year'
    },
    charts: {
      trend: 'Usage Trend',
      breakdown: 'Storage Breakdown'
    },
    monthlyHistory: {
      storage: 'Storage History',
      bandwidth: 'Bandwidth History',
      month: 'Month',
      usage: 'Usage',
      limit: 'Limit',
      change: 'Change',
      additionalCost: 'Extra Cost',
      total: 'Total',
      breakdown: 'Down/Up',
      peak: 'Peak Day'
    }
  }
}
